import { useCallback, useEffect, useMemo } from 'react'
import { Box, Portal } from '@chakra-ui/react'
import { motion, AnimatePresence, type MotionProps } from 'framer-motion'

const MotionBox = motion(Box)

export type StripeDirection =
  | 'vertical-down'
  | 'vertical-up'
  | 'horizontal-right'
  | 'horizontal-left'

export type StripeColorScheme =
  | 'purple'
  | 'blue'
  | 'green'
  | 'orange'
  | 'pink'
  | 'dark'
  | string

export interface StripeTransitionProps {
  isActive: boolean
  onComplete?: () => void
  stripeCount?: number
  duration?: number
  delay?: number
  direction?: StripeDirection
  colorScheme?: StripeColorScheme
  zIndex?: number
}

const colorSchemes: Record<string, string> = {
  purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  blue: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  green: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  orange: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  pink: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  dark: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
}

const EASING: [number, number, number, number] = [0.77, 0, 0.175, 1]

const StripeTransition = ({
  isActive,
  onComplete,
  stripeCount = 10,
  duration = 3,
  delay = 0.05,
  direction = 'vertical-down',
  colorScheme = 'purple',
  zIndex = 9999,
}: StripeTransitionProps) => {
  const stripes = useMemo(
    () => Array.from({ length: stripeCount }, (_, index) => index),
    [stripeCount]
  )

  const getBackground = useCallback(() => {
    return colorSchemes[colorScheme] || colorScheme
  }, [colorScheme])

  const getAnimationProps = useCallback(
    (index: number): MotionProps => {
      const baseDelay = index * delay

      switch (direction) {
        case 'vertical-down':
          return {
            initial: { y: '-100%' },
            animate: { y: ['0%', '0%', '100%'] },
            transition: {
              duration,
              delay: baseDelay,
              times: [0, 0.4, 1],
              ease: EASING,
            },
          }
        case 'vertical-up':
          return {
            initial: { y: '100%' },
            animate: { y: ['0%', '0%', '-100%'] },
            transition: {
              duration,
              delay: baseDelay,
              times: [0, 0.4, 1],
              ease: EASING,
            },
          }
        case 'horizontal-right':
          return {
            initial: { x: '-100%' },
            animate: { x: ['0%', '0%', '100%'] },
            transition: {
              duration,
              delay: baseDelay,
              times: [0, 0.4, 1],
              ease: EASING,
            },
          }
        case 'horizontal-left':
          return {
            initial: { x: '100%' },
            animate: { x: ['0%', '0%', '-100%'] },
            transition: {
              duration,
              delay: baseDelay,
              times: [0, 0.4, 1],
              ease: EASING,
            },
          }
        default:
          return {}
      }
    },
    [delay, direction, duration]
  )

  useEffect(() => {
    if (!isActive || !onComplete) {
      return
    }

    const totalDurationMs = (duration + (stripeCount - 1) * delay) * 1000
    const timer = window.setTimeout(() => {
      onComplete()
    }, totalDurationMs)

    return () => window.clearTimeout(timer)
  }, [delay, duration, isActive, onComplete, stripeCount])

  return (
    <Portal>
      <AnimatePresence>
        {isActive && (
          <Box
            position="fixed"
            top={0}
            left={0}
            width="100vw"
            height="100vh"
            zIndex={zIndex}
            pointerEvents="none"
          >
            <Box
              display="flex"
              width="100%"
              height="100%"
              flexDirection={direction.startsWith('vertical') ? 'row' : 'column'}
            >
              {stripes.map(index => (
                <MotionBox
                  key={index}
                  flex="1"
                  height="100%"
                  background={getBackground()}
                  {...getAnimationProps(index)}
                />
              ))}
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </Portal>
  )
}

export default StripeTransition
